/* ===========================================================================
   LMP Shipping, UI behaviors.
   Dark-mode toggle (persisted) · header hamburger + search · geo-aware hero ·
   stats count-up · FAQ-backed AI help/chat widget. Progressive enhancement.
   =========================================================================== */
(function () {
  'use strict';
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Header: hamburger + frosted-glass on scroll ----------------------- */
  function initHeader() {
    var burger = document.querySelector('[data-lmp-hamburger]');
    var menu = document.querySelector('[data-lmp-mobile-menu]');
    if (burger && menu) {
      burger.addEventListener('click', function () {
        var open = menu.classList.toggle('open');
        burger.setAttribute('aria-expanded', String(open));
      });
      menu.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function () { menu.classList.remove('open'); burger.setAttribute('aria-expanded', 'false'); });
      });
    }
    var header = document.querySelector('.lmp-header');
    if (header) {
      var onScroll = function () { header.classList.toggle('is-scrolled', (window.scrollY || window.pageYOffset) > 24); };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---- Geo/behavior-aware hero ------------------------------------------- */
  function detectRegion() {
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (/Jerusalem|Tel_Aviv/i.test(tz)) return 'IL';
      var lang = (navigator.language || '').toLowerCase();
      if (lang.indexOf('he') === 0) return 'IL';
      if (/America|US|New_York|Chicago|Los_Angeles|Denver/i.test(tz)) return 'US';
    } catch (e) {}
    return null;
  }
  function initGeoHero() {
    var region = detectRegion();
    if (!region) return;
    document.querySelectorAll('[data-lmp-geo]').forEach(function (el) {
      var msg = el.getAttribute('data-lmp-geo-' + region.toLowerCase());
      if (msg) el.textContent = msg;
    });
    document.documentElement.setAttribute('data-lmp-region', region);
  }

  /* ---- Stats count-up ---------------------------------------------------- */
  function initStats() {
    var nums = document.querySelectorAll('[data-lmp-countup]');
    if (!nums.length) return;
    function run(el) {
      var raw = el.getAttribute('data-lmp-countup') || '0';
      var target = parseFloat(raw) || 0;
      var decimals = (raw.split('.')[1] || '').length; // preserve e.g. 4.8 → "4.8"
      var suffix = el.getAttribute('data-lmp-suffix') || '';
      var prefix = el.getAttribute('data-lmp-prefix') || '';
      var fmt = function (v) {
        return decimals > 0
          ? v.toFixed(decimals)
          : Math.round(v).toLocaleString('en-US');
      };
      if (REDUCED || !target) { el.textContent = prefix + fmt(target) + suffix; return; }
      var dur = 1400, start = null;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var e = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + fmt(target * e) + suffix;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if (!('IntersectionObserver' in window)) { nums.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } });
    }, { threshold: 0.4 });
    nums.forEach(function (n) { io.observe(n); });
  }

  /* ---- AI help / chat widget (FAQ-backed scaffold) ----------------------- */
  var FAQ = [
    { q: /vat|tax/i, a: 'Israeli VAT (18%) is shown as a separate "added later" line, never folded into the headline estimate. It applies to the goods value above the de-minimis.' },
    { q: /how (long|much time)|transit|deliver|6 month|when/i, a: 'Sea freight only, transit can take up to ~6 months. We do not promise delivery dates, but you can track your shipment through every stage.' },
    { q: /how much|cost|price|estimate|quote/i, a: 'Your headline estimate is total volume (CBM) × $300. Paste a product link or your invoice and we calculate it in seconds. Israeli VAT and local delivery are shown separately.' },
    { q: /customs|clear/i, a: 'We handle US export paperwork and Israeli customs clearance for you, no logistics expertise needed on your end.' },
    { q: /prohibit|excluded|can.?t ship|aerosol|batter|flammable/i, a: 'Some items can’t go by sea: aerosols, lithium batteries, flammables, food/perishables, hazardous chemicals, and certain liquids.' },
    { q: /insurance/i, a: 'Optional insurance is available; coverage is a small percentage of the item value. Ask us when you request your estimate.' },
    { q: /pickup|newark|warehouse/i, a: 'Already have the item in the US? Use Local Pickup: send a photo, size and weight, and we’ll quote bringing it to our Newark, NJ warehouse.' },
    { q: /track/i, a: 'Track your shipment day or night: warehouse received → consolidated → on the water → Israeli customs → out for delivery.' },
  ];
  function answer(text) {
    var hit = FAQ.find(function (f) { return f.q.test(text); });
    return hit ? hit.a : 'Thanks! A team member will follow up. Meanwhile, paste a product link on the home page to get an instant sea-freight estimate, or ask about VAT, transit time, customs, or pickup.';
  }
  function initChat() {
    var fab = document.querySelector('[data-lmp-chat-fab]');
    var panel = document.querySelector('[data-lmp-chat-panel]');
    if (!fab || !panel) return;
    var body = panel.querySelector('[data-lmp-chat-body]');
    var form = panel.querySelector('[data-lmp-chat-form]');
    var input = form && form.querySelector('input');

    function toggle(open) {
      panel.classList.toggle('open', open);
      fab.setAttribute('aria-expanded', String(open));
      if (open && input) input.focus();
    }
    fab.addEventListener('click', function () { toggle(!panel.classList.contains('open')); });
    panel.querySelectorAll('[data-lmp-chat-close]').forEach(function (b) { b.addEventListener('click', function () { toggle(false); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') toggle(false); });

    function push(text, who) {
      var m = document.createElement('div');
      m.className = 'lmp-chat-msg ' + who;
      m.textContent = text;
      body.appendChild(m);
      body.scrollTop = body.scrollHeight;
    }
    function ask(text) { push(text, 'user'); setTimeout(function () { push(answer(text), 'bot'); }, 250); }

    panel.querySelectorAll('[data-lmp-chat-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () { ask(chip.textContent); });
    });
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = input.value.trim();
      if (!v) return;
      input.value = '';
      ask(v);
    });
  }

  /* ---- Store logos by domain (provider chain → monogram fallback) -------- */
  function initLogos() {
    document.querySelectorAll('[data-lmp-logo]').forEach(function (img) {
      var srcs = (img.getAttribute('data-srcs') || '').split('|').filter(Boolean);
      var i = 0;
      function next() {
        if (i < srcs.length) {
          img.src = srcs[i++];
        } else {
          img.style.display = 'none';
          var mono = img.parentNode && img.parentNode.querySelector('.lmp-logo-mono');
          if (mono) mono.hidden = false;
        }
      }
      img.addEventListener('error', next);
      next();
    });
  }

  /* ---- How-it-works cubicles: tap-to-expand (mobile), a11y ---------------- */
  function initHiwCubes() {
    document.querySelectorAll('[data-lmp-expand] .lmp-hiw-toggle').forEach(function (btn) {
      var cube = btn.closest('[data-lmp-expand]');
      btn.addEventListener('click', function () {
        var open = cube.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* ---- Prohibited-items tiles: open on hover (pointer devices), no click
     needed. Touch/keyboard still work via the native <details> click/Enter. --- */
  function initProhibitHover() {
    if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;
    document.querySelectorAll('.lmp-prohibit').forEach(function (d) {
      d.addEventListener('mouseenter', function () { d.open = true; });
      d.addEventListener('mouseleave', function () { d.open = false; });
    });
  }

  /* ---- FAQ: one-open-at-a-time + deep-link (#faq-…) ----------------------- */
  function initFaq() {
    document.querySelectorAll('[data-lmp-faq-group]').forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('details.lmp-faq'));
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (d.open) items.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });
    // Open + scroll to a deep-linked answer on load / hash change.
    function openHash() {
      var id = location.hash.slice(1);
      if (!id) return;
      var el = document.getElementById(id);
      if (el && el.tagName === 'DETAILS') {
        var group = el.closest('[data-lmp-faq-group]');
        if (group) group.querySelectorAll('details.lmp-faq').forEach(function (o) { if (o !== el) o.open = false; });
        el.open = true;
        el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      }
    }
    openHash();
    window.addEventListener('hashchange', openHash);
  }

  /* ---- How-it-works sticky mini-nav: highlight current step -------------- */
  function initHiwNav() {
    var nav = document.querySelector('[data-lmp-hiw-nav]');
    if (!nav || !('IntersectionObserver' in window)) return;
    var links = {};
    nav.querySelectorAll('a').forEach(function (a) { links[a.getAttribute('data-step')] = a; });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = (e.target.id || '').replace('step-', '');
        nav.querySelectorAll('a').forEach(function (a) { a.classList.remove('is-current'); });
        if (links[n]) links[n].classList.add('is-current');
      });
    }, { rootMargin: '-45% 0px -45% 0px' });
    document.querySelectorAll('.lmp-hiw-cube[id^="step-"]').forEach(function (c) { io.observe(c); });
  }

  /* ---- Dark mode toggle (persisted; falls back to OS preference) --------- */
  function effectiveTheme() {
    var attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    return (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
  }
  function paintToggle(t) {
    document.querySelectorAll('[data-lmp-theme-toggle]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(t === 'dark'));
      b.textContent = t === 'dark' ? '☀️' : '🌙';
      b.setAttribute('aria-label', t === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
    });
  }
  function initTheme() {
    paintToggle(effectiveTheme());
    document.querySelectorAll('[data-lmp-theme-toggle]').forEach(function (b) {
      b.addEventListener('click', function () {
        var next = effectiveTheme() === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        try { localStorage.setItem('lmp-theme', next); } catch (e) {}
        paintToggle(next);
      });
    });
  }

  function init() {
    initTheme();
    initHeader();
    initLogos();
    initGeoHero();
    initStats();
    initChat();
    initHiwCubes();
    initProhibitHover();
    initFaq();
    initHiwNav();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
